import React, { useState, useEffect, useMemo } from 'react';
import { directMessageService } from '../services/directMessageService';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import './DirectMessages.css';

const DirectMessages = () => {
  const [messages, setMessages] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [groups, setGroups] = useState([
    { id: 'all-drivers', name: 'All Drivers', icon: '🚌' },
    { id: 'all-office', name: 'Office Staff', icon: '🏢' },
    { id: 'all-mechanics', name: 'Mechanics', icon: '🔧' },
  ]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [showRecipientPicker, setShowRecipientPicker] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [messageStatus, setMessageStatus] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('contacts'); // 'contacts' or 'groups'
  
  // Get current user from AuthContext
  const { user } = useAuth();
  const userRole = (user?.role || '').toLowerCase();
  const canViewAcknowledgments = ['admin', 'general_manager'].includes(userRole);
  const isAdmin = userRole === 'admin';
  
  // New group form
  const [newGroup, setNewGroup] = useState({ name: '', icon: '👥', member_ids: [] });
  
  // New message form state
  const [newMessage, setNewMessage] = useState({
    subject: '',
    content: '',
    priority: 'normal',
    recipient_ids: [],
    send_to_all: false
  });

  useEffect(() => {
    fetchMessages();
    fetchEmployees();
    loadGroups();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const data = await directMessageService.getAll({ days: 30 });
      setMessages(data.messages || []);
    } catch (err) {
      setError('Failed to load messages');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/users');
      // Get all active users (employees, drivers, etc.)
      const allUsers = (response.data.users || []).filter(u => u.is_active !== false);
      setEmployees(allUsers);
    } catch (err) {
      console.error('Failed to fetch employees:', err);
    }
  };

  const loadGroups = () => {
    // Load custom groups from localStorage
    const savedGroups = localStorage.getItem('dm_groups');
    if (savedGroups) {
      setGroups(prev => [...prev, ...JSON.parse(savedGroups)]);
    }
  };

  const saveGroup = () => {
    if (!newGroup.name || newGroup.member_ids.length === 0) return;
    
    const customGroup = {
      id: `custom-${Date.now()}`,
      name: newGroup.name,
      icon: newGroup.icon,
      member_ids: newGroup.member_ids,
      isCustom: true
    };
    
    const customGroups = groups.filter(g => g.isCustom);
    customGroups.push(customGroup);
    localStorage.setItem('dm_groups', JSON.stringify(customGroups));
    
    setGroups(prev => [...prev, customGroup]);
    setNewGroup({ name: '', icon: '👥', member_ids: [] });
    setShowGroupModal(false);
  };

  const deleteGroup = (groupId) => {
    const updatedGroups = groups.filter(g => g.id !== groupId);
    const customGroups = updatedGroups.filter(g => g.isCustom);
    localStorage.setItem('dm_groups', JSON.stringify(customGroups));
    setGroups(updatedGroups);
  };

  // Group employees alphabetically
  const groupedEmployees = useMemo(() => {
    const filtered = employees.filter(emp => 
      emp.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    const grouped = {};
    filtered.forEach(emp => {
      const firstLetter = (emp.name?.[0] || '?').toUpperCase();
      if (!grouped[firstLetter]) {
        grouped[firstLetter] = [];
      }
      grouped[firstLetter].push(emp);
    });
    
    // Sort each group by name
    Object.keys(grouped).forEach(letter => {
      grouped[letter].sort((a, b) => a.name?.localeCompare(b.name));
    });
    
    return grouped;
  }, [employees, searchTerm]);

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  const availableLetters = Object.keys(groupedEmployees).sort();

  const scrollToLetter = (letter) => {
    const element = document.getElementById(`section-${letter}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    try {
      await directMessageService.send(newMessage);
      setShowNewMessage(false);
      setNewMessage({
        subject: '',
        content: '',
        priority: 'normal',
        recipient_ids: [],
        send_to_all: false
      });
      fetchMessages();
    } catch (err) {
      setError('Failed to send message');
      console.error(err);
    }
  };

  const viewMessageStatus = async (message) => {
    try {
      setSelectedMessage(message);
      const data = await directMessageService.getMessageStatus(message.id);
      setMessageStatus(data);
      setShowStatusModal(true);
    } catch (err) {
      setError('Failed to load message status');
      console.error(err);
    }
  };

  const handleResend = async (messageId) => {
    try {
      await directMessageService.resend(messageId);
      if (selectedMessage && selectedMessage.id === messageId) {
        const data = await directMessageService.getMessageStatus(messageId);
        setMessageStatus(data);
      }
      fetchMessages();
    } catch (err) {
      setError('Failed to resend message');
      console.error(err);
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('Are you sure you want to permanently delete this message? This cannot be undone.')) {
      return;
    }
    try {
      await directMessageService.delete(messageId);
      fetchMessages();
      if (showStatusModal && selectedMessage?.id === messageId) {
        setShowStatusModal(false);
      }
    } catch (err) {
      setError('Failed to delete message');
      console.error(err);
    }
  };

  const handleRecipientToggle = (employeeId) => {
    setNewMessage(prev => ({
      ...prev,
      recipient_ids: prev.recipient_ids.includes(employeeId)
        ? prev.recipient_ids.filter(id => id !== employeeId)
        : [...prev.recipient_ids, employeeId]
    }));
  };

  const handleGroupSelect = (group) => {
    let memberIds = [];
    
    if (group.member_ids) {
      // Custom group with saved member IDs
      memberIds = group.member_ids;
    } else {
      // Default groups - filter by role/department
      switch (group.id) {
        case 'all-drivers':
          memberIds = employees.filter(e => e.role === 'driver').map(e => e.id);
          break;
        case 'all-office':
          memberIds = employees.filter(e => e.department === 'Office' || e.role === 'employee').map(e => e.id);
          break;
        case 'all-mechanics':
          memberIds = employees.filter(e => e.department === 'Maintenance' || e.position?.toLowerCase().includes('mechanic')).map(e => e.id);
          break;
        default:
          break;
      }
    }
    
    setNewMessage(prev => ({
      ...prev,
      recipient_ids: [...new Set([...prev.recipient_ids, ...memberIds])]
    }));
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getAvatarColor = (name) => {
    const colors = ['#3B82F6', '#8B5CF6', '#EC4899', '#D4A94E', '#10B981', '#6366F1', '#EF4444'];
    const index = (name?.charCodeAt(0) || 0) % colors.length;
    return colors[index];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return '#DC2626';
      case 'high': return '#D4A94E';
      case 'normal': return '#3B82F6';
      case 'low': return '#6B7280';
      default: return '#3B82F6';
    }
  };

  const selectedCount = newMessage.recipient_ids.length;

  if (loading) {
    return <div className="dm-loading">Loading messages...</div>;
  }

  return (
    <div className="direct-messages">
      <div className="dm-header">
        <div>
          <h1>📨 Direct Messages</h1>
          <p>Send tracked messages to employees with acknowledgment</p>
        </div>
        <div className="dm-header-actions">
          <button className="dm-group-btn" onClick={() => setShowGroupModal(true)}>
            👥 Manage Groups
          </button>
          <button className="dm-new-btn" onClick={() => setShowNewMessage(true)}>
            + New Message
          </button>
        </div>
      </div>

      {error && <div className="dm-error">{error}</div>}

      {/* Messages List */}
      <div className="dm-list">
        <table className="dm-table">
          <thead>
            <tr>
              <th>Subject</th>
              <th>Priority</th>
              <th>Sent</th>
              <th>Recipients</th>
              <th>Acknowledged</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {messages.length === 0 ? (
              <tr>
                <td colSpan="6" className="dm-empty">No messages sent yet</td>
              </tr>
            ) : (
              messages.map(msg => (
                <tr key={msg.id}>
                  <td className="dm-subject">{msg.subject}</td>
                  <td>
                    <span 
                      className="dm-priority-badge"
                      style={{ backgroundColor: getPriorityColor(msg.priority) }}
                    >
                      {msg.priority?.toUpperCase()}
                    </span>
                  </td>
                  <td>{formatDate(msg.created_at)}</td>
                  <td>{msg.total_recipients}</td>
                  <td>
                    <span className={`dm-ack-status ${Number(msg.acknowledged_count) === Number(msg.total_recipients) ? 'complete' : 'pending'}`}>
                      {msg.acknowledged_count || 0} / {msg.total_recipients || 0}
                    </span>
                  </td>
                  <td className="dm-actions-cell">
                    {/* Only Admin and General Manager can view acknowledgment details */}
                    {canViewAcknowledgments && (
                      <button 
                        className="dm-action-btn view"
                        onClick={() => viewMessageStatus(msg)}
                      >
                        View Status
                      </button>
                    )}
                    {Number(msg.acknowledged_count) < Number(msg.total_recipients) && (
                      <button 
                        className="dm-action-btn resend"
                        onClick={() => handleResend(msg.id)}
                      >
                        Resend
                      </button>
                    )}
                    {/* Only Admin can delete messages */}
                    {isAdmin && (
                      <button 
                        className="dm-action-btn delete"
                        onClick={() => handleDelete(msg.id)}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* New Message Modal */}
      {showNewMessage && (
        <div className="dm-modal-overlay" onClick={() => setShowNewMessage(false)}>
          <div className="dm-modal dm-compose-modal" onClick={e => e.stopPropagation()}>
            <div className="dm-modal-header">
              <h2>📝 New Direct Message</h2>
              <button className="dm-close-btn" onClick={() => setShowNewMessage(false)}>×</button>
            </div>
            <form onSubmit={handleSendMessage}>
              {/* Recipients Section */}
              <div className="dm-form-group">
                <label>To:</label>
                <div className="dm-recipients-display" onClick={() => setShowRecipientPicker(true)}>
                  {selectedCount === 0 ? (
                    <span className="dm-placeholder">Tap to select recipients...</span>
                  ) : (
                    <span className="dm-selected-count">
                      {selectedCount} recipient{selectedCount !== 1 ? 's' : ''} selected
                    </span>
                  )}
                  <span className="dm-picker-arrow">›</span>
                </div>
              </div>

              <div className="dm-form-group">
                <label>Subject *</label>
                <input
                  type="text"
                  value={newMessage.subject}
                  onChange={e => setNewMessage({...newMessage, subject: e.target.value})}
                  required
                  placeholder="Enter message subject"
                />
              </div>
              
              <div className="dm-form-group">
                <label>Message *</label>
                <textarea
                  value={newMessage.content}
                  onChange={e => setNewMessage({...newMessage, content: e.target.value})}
                  required
                  placeholder="Enter your message"
                  rows={4}
                />
              </div>

              <div className="dm-form-group">
                <label>Priority</label>
                <select
                  value={newMessage.priority}
                  onChange={e => setNewMessage({...newMessage, priority: e.target.value})}
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div className="dm-form-actions">
                <button type="button" className="dm-cancel-btn" onClick={() => setShowNewMessage(false)}>
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="dm-submit-btn"
                  disabled={!newMessage.subject || !newMessage.content || selectedCount === 0}
                >
                  Send Message
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* iPhone-Style Recipient Picker */}
      {showRecipientPicker && (
        <div className="dm-modal-overlay" onClick={() => setShowRecipientPicker(false)}>
          <div className="dm-contact-picker" onClick={e => e.stopPropagation()}>
            <div className="dm-picker-header">
              <button className="dm-picker-cancel" onClick={() => setShowRecipientPicker(false)}>
                Cancel
              </button>
              <h3>Select Recipients</h3>
              <button 
                className="dm-picker-done" 
                onClick={() => setShowRecipientPicker(false)}
              >
                Done ({selectedCount})
              </button>
            </div>

            {/* Search Bar */}
            <div className="dm-search-bar">
              <span className="dm-search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="dm-search-clear" onClick={() => setSearchTerm('')}>×</button>
              )}
            </div>

            {/* Tabs */}
            <div className="dm-picker-tabs">
              <button 
                className={`dm-tab ${activeTab === 'contacts' ? 'active' : ''}`}
                onClick={() => setActiveTab('contacts')}
              >
                Contacts
              </button>
              <button 
                className={`dm-tab ${activeTab === 'groups' ? 'active' : ''}`}
                onClick={() => setActiveTab('groups')}
              >
                Groups
              </button>
            </div>

            {activeTab === 'contacts' ? (
              <div className="dm-contacts-container">
                {/* Contact List */}
                <div className="dm-contact-list">
                  {/* Select All Option */}
                  <div 
                    className={`dm-contact-item dm-select-all ${newMessage.send_to_all ? 'selected' : ''}`}
                    onClick={() => {
                      const allIds = employees.map(e => e.id);
                      setNewMessage(prev => ({
                        ...prev,
                        send_to_all: !prev.send_to_all,
                        recipient_ids: prev.send_to_all ? [] : allIds
                      }));
                    }}
                  >
                    <div className="dm-contact-avatar" style={{ background: '#D4A94E' }}>
                      👥
                    </div>
                    <div className="dm-contact-info">
                      <div className="dm-contact-name">Select All Employees</div>
                      <div className="dm-contact-detail">{employees.length} people</div>
                    </div>
                    <div className={`dm-contact-check ${newMessage.send_to_all ? 'checked' : ''}`}>
                      {newMessage.send_to_all && '✓'}
                    </div>
                  </div>

                  {/* Alphabetical Sections */}
                  {availableLetters.map(letter => (
                    <div key={letter} id={`section-${letter}`}>
                      <div className="dm-section-header">{letter}</div>
                      {groupedEmployees[letter].map(emp => (
                        <div 
                          key={emp.id}
                          className={`dm-contact-item ${newMessage.recipient_ids.includes(emp.id) ? 'selected' : ''}`}
                          onClick={() => handleRecipientToggle(emp.id)}
                        >
                          <div 
                            className="dm-contact-avatar"
                            style={{ background: getAvatarColor(emp.name) }}
                          >
                            {getInitials(emp.name)}
                          </div>
                          <div className="dm-contact-info">
                            <div className="dm-contact-name">{emp.name}</div>
                            <div className="dm-contact-detail">{emp.position || emp.role || emp.email}</div>
                          </div>
                          <div className={`dm-contact-check ${newMessage.recipient_ids.includes(emp.id) ? 'checked' : ''}`}>
                            {newMessage.recipient_ids.includes(emp.id) && '✓'}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                {/* Alphabet Index */}
                <div className="dm-alphabet-index">
                  {alphabet.map(letter => (
                    <div
                      key={letter}
                      className={`dm-index-letter ${availableLetters.includes(letter) ? 'active' : ''}`}
                      onClick={() => availableLetters.includes(letter) && scrollToLetter(letter)}
                    >
                      {letter}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              /* Groups Tab */
              <div className="dm-groups-list">
                {groups.map(group => (
                  <div 
                    key={group.id}
                    className="dm-group-item"
                    onClick={() => handleGroupSelect(group)}
                  >
                    <div className="dm-group-icon">{group.icon}</div>
                    <div className="dm-group-info">
                      <div className="dm-group-name">{group.name}</div>
                      <div className="dm-group-count">
                        {group.member_ids?.length || 'All matching'} members
                      </div>
                    </div>
                    <span className="dm-group-add">+ Add</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Manage Groups Modal */}
      {showGroupModal && (
        <div className="dm-modal-overlay" onClick={() => setShowGroupModal(false)}>
          <div className="dm-modal" onClick={e => e.stopPropagation()}>
            <div className="dm-modal-header">
              <h2>👥 Manage Groups</h2>
              <button className="dm-close-btn" onClick={() => setShowGroupModal(false)}>×</button>
            </div>
            
            <div className="dm-group-management">
              <h4>Create New Group</h4>
              <div className="dm-form-group">
                <label>Group Name</label>
                <input
                  type="text"
                  value={newGroup.name}
                  onChange={e => setNewGroup({...newGroup, name: e.target.value})}
                  placeholder="e.g., Morning Shift Drivers"
                />
              </div>
              
              <div className="dm-form-group">
                <label>Icon</label>
                <div className="dm-icon-picker">
                  {['👥', '🚌', '🏢', '🔧', '⭐', '📋', '🎯', '💼'].map(icon => (
                    <span
                      key={icon}
                      className={`dm-icon-option ${newGroup.icon === icon ? 'selected' : ''}`}
                      onClick={() => setNewGroup({...newGroup, icon})}
                    >
                      {icon}
                    </span>
                  ))}
                </div>
              </div>

              <div className="dm-form-group">
                <label>Members ({newGroup.member_ids.length} selected)</label>
                <div className="dm-group-members-list">
                  {employees.map(emp => (
                    <label key={emp.id} className="dm-member-checkbox">
                      <input
                        type="checkbox"
                        checked={newGroup.member_ids.includes(emp.id)}
                        onChange={() => {
                          setNewGroup(prev => ({
                            ...prev,
                            member_ids: prev.member_ids.includes(emp.id)
                              ? prev.member_ids.filter(id => id !== emp.id)
                              : [...prev.member_ids, emp.id]
                          }));
                        }}
                      />
                      {emp.name}
                    </label>
                  ))}
                </div>
              </div>

              <button 
                className="dm-submit-btn"
                onClick={saveGroup}
                disabled={!newGroup.name || newGroup.member_ids.length === 0}
              >
                Create Group
              </button>

              {/* Existing Custom Groups */}
              {groups.filter(g => g.isCustom).length > 0 && (
                <>
                  <h4 style={{ marginTop: '24px' }}>Your Custom Groups</h4>
                  {groups.filter(g => g.isCustom).map(group => (
                    <div key={group.id} className="dm-existing-group">
                      <span>{group.icon} {group.name}</span>
                      <span className="dm-group-member-count">{group.member_ids?.length} members</span>
                      <button 
                        className="dm-delete-group"
                        onClick={() => deleteGroup(group.id)}
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Message Status Modal - Only visible to Admin and General Manager */}
      {showStatusModal && messageStatus && canViewAcknowledgments && (
        <div className="dm-modal-overlay" onClick={() => setShowStatusModal(false)}>
          <div className="dm-modal dm-status-modal" onClick={e => e.stopPropagation()}>
            <div className="dm-modal-header">
              <h2>📊 Message Status</h2>
              <button className="dm-close-btn" onClick={() => setShowStatusModal(false)}>×</button>
            </div>
            
            <div className="dm-status-content">
              <div className="dm-status-message">
                <h3>{messageStatus.message?.subject}</h3>
                <p>{messageStatus.message?.content}</p>
                <div className="dm-status-meta">
                  <span>Sent by: {messageStatus.message?.sender_name}</span>
                  <span>Date: {formatDate(messageStatus.message?.created_at)}</span>
                </div>
              </div>

              <div className="dm-status-summary">
                <div className="dm-stat-card total">
                  <span className="dm-stat-number">{messageStatus.summary?.total}</span>
                  <span className="dm-stat-label">Total Recipients</span>
                </div>
                <div className="dm-stat-card acknowledged">
                  <span className="dm-stat-number">{messageStatus.summary?.acknowledged}</span>
                  <span className="dm-stat-label">Acknowledged</span>
                </div>
                <div className="dm-stat-card pending">
                  <span className="dm-stat-number">{messageStatus.summary?.pending}</span>
                  <span className="dm-stat-label">Pending</span>
                </div>
              </div>

              <div className="dm-recipients-status">
                <h4>Recipients</h4>
                <table className="dm-recipients-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Email</th>
                      <th>Acknowledged</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {messageStatus.recipients?.map(r => (
                      <tr key={r.id} className={r.acknowledged_at ? 'acknowledged' : 'pending'}>
                        <td>{r.recipient_name}</td>
                        <td>{r.recipient_email}</td>
                        <td>{r.acknowledged_at ? formatDate(r.acknowledged_at) : '-'}</td>
                        <td>
                          {r.acknowledged_at ? (
                            <span className="dm-status-badge acknowledged">✓ Acknowledged</span>
                          ) : (
                            <span className="dm-status-badge pending">⏳ Pending</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="dm-status-actions">
                {messageStatus.summary?.pending > 0 && (
                  <button 
                    className="dm-resend-all-btn"
                    onClick={() => handleResend(messageStatus.message?.id)}
                  >
                    🔄 Resend to {messageStatus.summary?.pending} Pending Recipients
                  </button>
                )}
                {isAdmin && (
                  <button 
                    className="dm-delete-message-btn"
                    onClick={() => handleDelete(messageStatus.message?.id)}
                  >
                    🗑️ Delete Message
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DirectMessages;
